import { HttpClient } from '@adapters/HttpClient';
import type {
  Courier,
  ShipmentStatus,
  ShipmentStatusMapping,
  ShipmentStatusMappingFilters,
  ShipmentStatusMappingListResponse,
  ShipmentStatusListResponse,
  MapShipmentStatusRequest,
} from '../models/shipmentStatusMapping.types';

export interface ShipmentStatusMappingPage {
  mappings: ShipmentStatusMapping[];
  nextPage?: number;
  hasMore: boolean;
  total: number;
}

interface CouriersResponse {
  data: Courier[];
}

export class ShipmentStatusMappingAdapter {
  constructor(private httpClient: HttpClient) {}

  async fetchCouriers(): Promise<Courier[]> {
    const response = await this.httpClient.get<CouriersResponse>('/couriers');
    return response.data;
  }

  async fetchShipmentStatuses(): Promise<ShipmentStatus[]> {
    const response =
      await this.httpClient.get<ShipmentStatusListResponse>('/shipment-statuses');
    return response.data;
  }

  async fetchShipmentStatusMappingsPaginated(
    filters: ShipmentStatusMappingFilters = {}
  ): Promise<ShipmentStatusMappingPage> {
    const params = this.buildQueryParams(filters);

    const response = await this.httpClient.get<ShipmentStatusMappingListResponse>(
      '/shipment-status-mappings',
      params,
      {},
      filters.page === 1
    );
    return this.mapToPage(response);
  }

  async fetchShipmentStatusMappingById(id: number): Promise<ShipmentStatusMapping> {
    return await this.httpClient.get<ShipmentStatusMapping>(
      `/shipment-status-mappings/${id}`
    );
  }

  async mapShipmentStatus(
    id: number,
    request: MapShipmentStatusRequest
  ): Promise<ShipmentStatusMapping> {
    return await this.httpClient.put<ShipmentStatusMapping>(
      `/shipment-status-mappings/${id}/map`,
      request
    );
  }

  async unmapShipmentStatus(id: number): Promise<ShipmentStatusMapping> {
    return this.mapShipmentStatus(id, { shipment_status_id: null });
  }

  private buildQueryParams(
    filters: ShipmentStatusMappingFilters
  ): Record<string, string | number | number[] | null | undefined> {
    const params: Record<string, string | number | number[] | null | undefined> = {};

    if (filters.courier_id) params['courier_ids[]'] = filters.courier_id;
    if (filters.shipment_status_id !== undefined)
      params.shipment_status_id = filters.shipment_status_id === null ? 'null' : filters.shipment_status_id;
    if (filters.search) params.search = filters.search;
    if (filters.page) params.page = filters.page;
    if (filters.per_page) params.per_page = filters.per_page;

    return params;
  }

  private mapToPage(
    response: ShipmentStatusMappingListResponse
  ): ShipmentStatusMappingPage {
    const { data, meta } = response;
    return {
      mappings: data,
      nextPage:
        meta.currentPage < meta.lastPage ? meta.currentPage + 1 : undefined,
      hasMore: meta.currentPage < meta.lastPage,
      total: meta.total,
    };
  }
}
